import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  useColorModeValue,
  SimpleGrid,
  Icon,
  Flex,
  useBreakpointValue,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Link,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useAccount } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConnectButton from '@/components/common/ConnectButton';

import {
  FiDollarSign,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiArrowRight,
  FiHome,
  FiLock,
  FiTarget,
  FiZap,
  FiCheckCircle,
  FiExternalLink,
} from 'react-icons/fi';
import Logo from '@/components/common/Logo';


const Welcome: React.FC = () => {
  const { isConnected } = useAccount();
  const navigate = useNavigate();
  const { t } = useTranslation('welcome');
  const currentPrice = 1.0; // 默认价格
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const isMobile = useBreakpointValue({ base: true, md: false });

  // 特点数据
  const features = [
    {
      icon: FiDollarSign,
      title: t('features.doubleReward.title'),
      description: t('features.doubleReward.description'),
      color: "green.500",
      highlight: false,
    },
    {
      icon: FiUsers,
      title: t('features.inviteMechanism.title'),
      description: t('features.inviteMechanism.description'),
      color: "blue.500",
      highlight: false,
    },
    {
      icon: FiShield,
      title: t('features.reserveRepurchase.title'),
      description: t('features.reserveRepurchase.description'),
      color: "orange.500",
      highlight: currentPrice < 0.99,
    },
    {
      icon: FiLock,
      title: t('features.fixedSupply.title'),
      description: t('features.fixedSupply.description'),
      color: "purple.500",
      highlight: false,
    },
  ];

  // 教程步骤
  const tutorialSteps = [
    {
      icon: FiHome,
      title: t('tutorial.steps.connectWallet.title'),
      description: t('tutorial.steps.connectWallet.description'),
      action: t('tutorial.steps.connectWallet.action'),
      link: "#connect",
    },
    {
      icon: FiDollarSign,
      title: t('tutorial.steps.rechargeUSDT.title'),
      description: t('tutorial.steps.rechargeUSDT.description'),
      action: t('tutorial.steps.rechargeUSDT.action'),
      link: "/recharge",
    },
    {
      icon: FiUsers,
      title: t('tutorial.steps.inviteFriends.title'),
      description: t('tutorial.steps.inviteFriends.description'),
      action: t('tutorial.steps.inviteFriends.action'),
      link: "/invite",
    },
    {
      icon: FiTarget,
      title: t('tutorial.steps.manageAssets.title'),
      description: t('tutorial.steps.manageAssets.description'),
      action: t('tutorial.steps.manageAssets.action'),
      link: "/management",
    },
  ];



  const handleEnterDashboard = () => {
    navigate('/');
  };

  const handleAction = (link: string) => {
    if (link === "#connect") {
      // 连接钱包逻辑已在ConnectButton中处理
      return;
    }
    navigate(link);
  };

  return (
    <Box minH="100vh" bg="gray.50" _dark={{ bg: 'gray.900' }}>
      {/* 英雄区 */}
      <Box 
        bgGradient="linear(to-b, blue.600, blue.400, blue.100)" 
        minH="80vh" 
        display="flex" 
        alignItems="center" 
        justifyContent="center"
        position="relative"
        overflow="hidden"
      >
        {/* 背景装饰 */}
        <Box
          position="absolute"
          top="10%"
          left="10%"
          w="200px"
          h="200px"
          bg="white"
          opacity="0.1"
          borderRadius="full"
          sx={{
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="15%"
          w="150px"
          h="150px"
          bg="white"
          opacity="0.1"
          borderRadius="full"
          sx={{
            animation: 'float 8s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        />

        <Container maxW="container.xl" py={20}>
          <VStack spacing={8} textAlign="center">
            <Logo size={isMobile ? "md" : "lg"} showText={true} />
            
            <VStack spacing={6}>
                             <Heading
                 as="h1"
                 size={{ base: "2xl", md: "4xl" }}
                 color="white"
                 fontWeight="bold"
                 textShadow="2px 2px 4px rgba(0,0,0,0.3)"
               >
                 {t('title')}
               </Heading>
               
               <Text fontSize={{ base: "lg", md: "xl" }} color="white" maxW="3xl" textShadow="2px 2px 4px rgba(0,0,0,0.5)" fontWeight="medium">
                 {t('subtitle')}
               </Text>



                             {/* 价格偏离警告 */}
               {currentPrice < 0.99 && (
                 <Alert status="warning" borderRadius="xl" maxW="md" bg="orange.50" border="1px" borderColor="orange.200">
                   <AlertIcon />
                   <Box>
                     <AlertTitle color="orange.800" fontWeight="bold">{t('priceDeviationWarning.title')}</AlertTitle>
                     <AlertDescription color="orange.700">
                       {t('priceDeviationWarning.description')}
                       <Button
                         size="sm"
                         variant="link"
                         colorScheme="orange"
                         ml={2}
                         fontWeight="bold"
                         onClick={() => navigate('/invite')}
                       >
                         {t('priceDeviationWarning.inviteButton')}
                       </Button>
                     </AlertDescription>
                   </Box>
                 </Alert>
               )}
            </VStack>

            {/* CTA 按钮 */}
                         <HStack spacing={4} flexWrap="wrap" justify="center">
               <Button
                 size="lg"
                 colorScheme="white"
                 variant="solid"
                 leftIcon={<FiDollarSign />}
                 onClick={() => navigate('/recharge')}
                 _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                 transition="all 0.2s"
               >
                 {t('heroButtons.recharge')}
               </Button>
               <Button
                 size="lg"
                 colorScheme="white"
                 variant="outline"
                 leftIcon={<FiArrowRight />}
                 onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                 _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                 transition="all 0.2s"
               >
                 {t('heroButtons.learnMore')}
               </Button>
             </HStack>
          </VStack>
        </Container>
      </Box>

             {/* 特点部分 */}
       <Box id="features" py={20} bg="white" _dark={{ bg: 'gray.800' }}>
         <Container maxW="container.xl">
           <VStack spacing={12}>
             <VStack spacing={4} textAlign="center">
               <Heading size="2xl" color="gray.800" _dark={{ color: 'white' }}>
                 {t('features.title')}
               </Heading>
               <Text fontSize="lg" color="gray.700" _dark={{ color: 'gray.300' }} maxW="2xl" fontWeight="medium">
                 {t('features.subtitle')}
               </Text>
             </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
              {features.map((feature, index) => (
                <Card
                  key={index}
                  bg={bg}
                  border="1px"
                  borderColor={feature.highlight ? "orange.200" : borderColor}
                  shadow="lg"
                  transition="all 0.3s"
                  _hover={{
                    transform: 'translateY(-8px)',
                    shadow: 'xl',
                    borderColor: feature.highlight ? "orange.300" : "blue.300",
                  }}
                  position="relative"
                >
                                     {feature.highlight && (
                     <Badge
                       position="absolute"
                       top={2}
                       right={2}
                       colorScheme="orange"
                       variant="solid"
                     >
                       {t('features.reserveRepurchase.currentRelevant')}
                     </Badge>
                   )}
                                     <CardBody textAlign="center" p={6}>
                     <Box
                       as={feature.icon}
                       w={12}
                       h={12}
                       color={feature.color}
                       mb={4}
                     />
                     <Heading size="md" mb={3} color="gray.800" _dark={{ color: 'white' }}>
                       {feature.title}
                     </Heading>
                     <Text color="gray.700" _dark={{ color: 'gray.300' }} fontSize="sm" lineHeight="tall" fontWeight="medium">
                       {feature.description}
                     </Text>
                   </CardBody>
                </Card>
              ))}
            </SimpleGrid>

                         <Button
               size="lg"
               colorScheme="blue"
               leftIcon={<FiArrowRight />}
               onClick={handleEnterDashboard}
               _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
               transition="all 0.2s"
             >
               {t('features.startButton')}
             </Button>
          </VStack>
        </Container>
      </Box>

             {/* 教程指南部分 */}
       <Box py={20} bg="gray.50" _dark={{ bg: 'gray.900' }}>
         <Container maxW="container.xl">
           <VStack spacing={12}>
             <VStack spacing={4} textAlign="center">
               <Heading size="2xl" color="gray.800" _dark={{ color: 'white' }}>
                 {t('tutorial.title')}
               </Heading>
               <Text fontSize="lg" color="gray.700" _dark={{ color: 'gray.300' }} maxW="2xl" fontWeight="medium">
                 {t('tutorial.subtitle')}
               </Text>
             </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="full">
              {tutorialSteps.map((step, index) => (
                <Card
                  key={index}
                  bg={bg}
                  border="1px"
                  borderColor={borderColor}
                  shadow="md"
                  transition="all 0.3s"
                  _hover={{
                    transform: 'translateY(-4px)',
                    shadow: 'lg',
                  }}
                >
                  <CardBody textAlign="center" p={6}>
                    <VStack spacing={4}>
                      <Box
                        w={16}
                        h={16}
                        bg="blue.500"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color="white"
                        fontSize="2xl"
                        fontWeight="bold"
                      >
                        {index + 1}
                      </Box>
                      <Box
                        as={step.icon}
                        w={8}
                        h={8}
                        color="blue.500"
                      />
                                             <VStack spacing={2}>
                         <Heading size="md" color="gray.800" _dark={{ color: 'white' }}>
                           {step.title}
                         </Heading>
                         <Text color="gray.700" _dark={{ color: 'gray.300' }} fontSize="sm" fontWeight="medium">
                           {step.description}
                         </Text>
                         <Button
                           size="sm"
                           colorScheme="blue"
                           variant="outline"
                           onClick={() => handleAction(step.link)}
                         >
                           {step.action}
                         </Button>
                       </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

             

             {/* CTA 区域 */}
       <Box 
         bgGradient="linear(to-r, blue.600, blue.700)" 
         py={20}
         textAlign="center"
       >
        <Container maxW="container.xl">
          <VStack spacing={8}>
                         <VStack spacing={4}>
               <Heading size="2xl" color="white" textShadow="2px 2px 4px rgba(0,0,0,0.3)">
                 {t('cta.title')}
               </Heading>
               <Text fontSize="lg" color="blue.50" maxW="2xl" fontWeight="medium" textShadow="1px 1px 2px rgba(0,0,0,0.3)">
                 {t('cta.subtitle')}
               </Text>
             </VStack>

                         <HStack spacing={4} flexWrap="wrap" justify="center">
               <Button
                 size="lg"
                 colorScheme="white"
                 variant="solid"
                 leftIcon={<FiDollarSign />}
                 onClick={() => navigate('/recharge')}
                 _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                 transition="all 0.2s"
               >
                 {t('cta.rechargeButton')}
               </Button>
               <Button
                 size="lg"
                 colorScheme="white"
                 variant="outline"
                 leftIcon={<FiUsers />}
                 onClick={() => navigate('/invite')}
                 _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                 transition="all 0.2s"
               >
                 {t('cta.inviteButton')}
               </Button>
             </HStack>
          </VStack>
        </Container>
      </Box>

             {/* 底部 */}
       <Box bg="blue.900" color="white" py={8}>
        <Container maxW="container.xl">
          <VStack spacing={4}>
            <HStack spacing={6} flexWrap="wrap" justify="center">
                             <Button
                 variant="link"
                 color="white"
                 onClick={() => navigate('/whitepaper')}
                 leftIcon={<FiExternalLink />}
                 _hover={{ textDecoration: 'underline' }}
               >
                 {t('footer.whitepaper')}
               </Button>
             </HStack>
             <Text fontSize="sm" color="blue.100" fontWeight="medium">
               {t('footer.copyright')}
             </Text>
          </VStack>
        </Container>
      </Box>


    </Box>
  );
};

export default Welcome; 